import { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/client';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import EmailService from '../services/EmailService';

const PaymentContext = createContext();

export function useRazorpay() {
  return useContext(PaymentContext);
}

export function RazorpayProvider({ children }) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [razorpayInstance, setRazorpayInstance] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  
  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        // Check if script is already loaded
        if (window.Razorpay) {
          console.log('Razorpay already loaded');
          setScriptLoaded(true);
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
          console.log('Razorpay SDK loaded successfully');
          setScriptLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          console.error('Razorpay SDK failed to load');
          setScriptLoaded(false);
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    const initializeRazorpay = async () => {
      const res = await loadRazorpayScript();
      if (!res) {
        console.error('Razorpay SDK failed to load');
      }
    };

    initializeRazorpay();
  }, []);
  
  const createPayment = async ({
    amount,
    bookingData,
    userId,
    onSuccess,
    onError
  }) => {
    try {
      // Log payment information for debugging
      console.log('PAYMENT AMOUNT CHECK:', {
        amount: amount,
        isFixed: amount === 500,
        isCOD: bookingData.paymentMethod === 'cod',
        displayAmount: `₹${amount.toLocaleString()}`
      });
      
      console.log('Starting payment process for:', {
        amount,
        bookingId: bookingData.id,
        paymentMethod: bookingData.paymentMethod,
        extraInfo: bookingData.advancePayment ? 'COD with advance payment' : 'Full payment'
      });
      
      // Extract the actual booking ID string
      const bookingId = bookingData.id;
      
      if (!bookingId || typeof bookingId !== 'string') {
        console.error('Invalid booking ID:', bookingId);
        throw new Error('Invalid booking ID format');
      }
      
      // Create a simplified payment object
      const paymentData = {
        amount: amount,
        bookingId: bookingId, // Use the validated booking ID
        userId: userId,
        timeCreated: new Date().toISOString()
      };
      
      // Store current payment for later use
      setCurrentPayment({
        paymentData,
        bookingData: {
          ...bookingData,
          id: bookingId // Ensure the ID is properly set
        },
        bookingId, // Store the validated booking ID
        userId,
        onSuccess,
        onError
      });
      
      // Function to open Razorpay - load script if not loaded
      const openRazorpayCheckout = async () => {
        // If Razorpay isn't loaded yet, try loading it again
        if (!window.Razorpay) {
          console.log("Razorpay not loaded, attempting to load...");
          
          // Try to load script dynamically
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          
          // Create a promise to wait for script to load
          const scriptPromise = new Promise((resolve, reject) => {
            script.onload = () => {
              console.log("Razorpay script loaded successfully via promise");
              resolve(true);
            };
            script.onerror = (e) => {
              console.error("Failed to load Razorpay SDK via promise:", e);
              reject(new Error('Failed to load Razorpay SDK'));
            };
          });
          
          document.body.appendChild(script);
          
          try {
            await scriptPromise;
            console.log("Razorpay script loaded successfully, window.Razorpay:", !!window.Razorpay);
          } catch (error) {
            console.error("Failed to load Razorpay script:", error);
            
            // Don't immediately call onError, try alternate loading method
            console.log("Trying alternate loading method for Razorpay");
            
            // Wait a moment and check if Razorpay loaded anyway
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            if (!window.Razorpay) {
              onError('Razorpay SDK failed to load. Please refresh and try again.');
              return;
            } else {
              console.log("Razorpay loaded after delay check");
            }
          }
        }
        
        if (!window.Razorpay) {
          console.error("Razorpay still not available after loading attempt");
          onError('Razorpay SDK failed to load');
          return;
        }
        
        // IMPORTANT: Use hardcoded key instead of process.env
        // Get Razorpay key (use test key for testing, live key for production)
        // Replace with your actual keys
        const key = "rzp_live_RxgW3j5BQcUHCr"; // Using test key directly for now
        
        console.log("Using Razorpay Key:", key);
        
        // Ensure we have customer contact info
        const customerName = bookingData.customerName || '';
        const customerEmail = bookingData.customerEmail || bookingData.email || '';
        const customerPhone = bookingData.customerPhone || bookingData.phone || '';
        
        // Log prefill data for debugging
        console.log("Razorpay prefill data:", {
          name: customerName,
          email: customerEmail,
          contact: customerPhone
        });
        
        // Create options for Razorpay
        const options = {
          key: key,
          amount: amount * 100, // Razorpay expects amount in paise
          currency: 'INR',
          name: 'Habba Events',
          description: bookingData.serviceName || 'Event Booking',
          image: 'https://habbaevents.com/icon.png', // Add your logo URL
          prefill: {
            name: customerName,
            email: customerEmail,
            contact: customerPhone
          },
          notes: {
            booking_id: bookingId
          },
          theme: {
            color: '#FF6EC7'
          },
          modal: {
            ondismiss: function() {
              console.log("Razorpay modal dismissed by user");
              // Don't immediately redirect on dismiss
              onError("Payment cancelled by user");
            }
          },
          handler: function(response) {
            // Validate response
            if (!response || !response.razorpay_payment_id) {
              console.error('Invalid payment response:', response);
              if (typeof options.onError === 'function') {
                options.onError('Invalid payment response received');
              }
              return;
            }

            // Store payment data in a variable for use in the handler
            const payment = {
              paymentData,
              bookingData,
              bookingId,
              userId,
              onSuccess,
              onError
            };

            // Validate payment data
            if (!payment.bookingId) {
              console.error('Missing booking ID in payment data');
              if (typeof onError === 'function') {
                onError('Invalid booking data');
              }
              return;
            }

            // This function will be called when payment is successful
            console.log("Payment successful:", response);
            
            try {
              // Pass the payment data to the handler
              processRazorpaySuccess(response, payment);
            } catch (error) {
              console.error("Error in payment handler:", error);
              processRazorpayFailure({ description: error.message || "Error processing payment" }, payment);
            }
          },
        };
        
        console.log("Creating Razorpay instance with options:", {
          key: options.key,
          amount: options.amount,
          currency: options.currency,
          name: options.name
        });
        
        try {
          // Create new Razorpay instance
          const razorpay = new window.Razorpay(options);
          
          // Store instance for potential later use
          setRazorpayInstance(razorpay);
          
          // On payment failure
          razorpay.on('payment.failed', function(response) {
            console.log("Payment failed event triggered:", response);
            
            // Store the current payment data for use in the failure handler
            const payment = {
              paymentData,
              bookingData,
              bookingId,
              userId,
              onSuccess,
              onError
            };
            processRazorpayFailure(response.error, payment);
          });
          
          // Open Razorpay checkout
          console.log("Opening Razorpay payment window...");
          razorpay.open();
          console.log("Razorpay payment window opened");
          
          // Return a promise that never resolves to prevent immediate navigation
          return new Promise(() => {});
        } catch (error) {
          console.error("Error creating or opening Razorpay:", error);
          onError(`Failed to initialize payment: ${error.message}`);
        }
      };
      
      // Open Razorpay checkout
      await openRazorpayCheckout();
      
    } catch (error) {
      console.error('Payment process error:', error);
      onError(`Payment process failed: ${error.message || 'Unknown error'}`);
    }
  };
  
  // Define new functions that don't rely on currentPayment state
  const processRazorpaySuccess = async (response, payment) => {
    try {
      if (!payment) {
        console.error('No payment data provided to processRazorpaySuccess');
        return;
      }
      
      const { bookingId, userId, bookingData, onSuccess } = payment;
      
      // Extract the actual booking ID string
      const actualBookingId = bookingId?.id || bookingId;

      if (!actualBookingId || typeof actualBookingId !== 'string') {
        console.error('Invalid booking ID:', { bookingId, actualBookingId });
        throw new Error('Invalid booking ID format');
      }
      
      console.log('Processing payment success:', {
        bookingId: actualBookingId,
        userId,
        amount: payment.paymentData ? payment.paymentData.amount : 'N/A',
        razorpayResponse: response
      });
      
      // Determine if this is a COD booking with advance payment
      const isCODWithAdvance = bookingData && 
        bookingData.paymentMethod === 'cod' && 
        bookingData.advancePayment;
      
      // Prepare the update object based on payment type
      const updateData = {
        status: 'confirmed',
        updatedAt: serverTimestamp(),
      };
      
      if (isCODWithAdvance) {
        // For COD with advance payment
        updateData.advancePaymentId = response.razorpay_payment_id;
        updateData.advancePaymentDate = serverTimestamp();
        updateData.paymentStatus = 'partial';
        updateData.advancePayment = bookingData.advancePayment;
        updateData.balanceAmount = bookingData.balanceAmount;
      } else {
        // For full payment
        updateData.paymentId = response.razorpay_payment_id;
        updateData.paymentDate = serverTimestamp();
        updateData.paymentStatus = 'completed';
      }
      
      // Update booking with payment details
      const bookingRef = doc(db, 'bookings', actualBookingId);
      console.log('Updating booking document:', { path: bookingRef.path, updateData });
      await updateDoc(bookingRef, updateData);
      
      console.log('Booking updated with payment details', updateData);
      
      // Try updating the user's booking copy too
      try {
        if (userId) {
          const userBookingRef = doc(db, 'users', userId.toString(), 'bookings', actualBookingId);
          await updateDoc(userBookingRef, updateData);
          console.log('User booking copy updated');
        }
      } catch (userBookingError) {
        console.error('Error updating user booking copy:', userBookingError);
        // Don't throw error here as this is a secondary operation
      }
      
      // Reset payment state
      setCurrentPayment(null);
      
      // Call the success callback
      if (typeof onSuccess === 'function') {
        onSuccess(response);
      } else {
        console.error('onSuccess callback is not a function');
      }
    } catch (error) {
      console.error('Error completing payment:', error);
      if (payment && payment.onError) {
        payment.onError(error.message || 'Error processing payment');
      }
    }
  };
  
  const processRazorpayFailure = (error, payment) => {
    try {
      const errorMessage = error.description || error.message || 'Payment failed';
      console.error('Payment failure details:', {
        error: error,
        errorMessage: errorMessage,
        payment: payment ? {
          bookingId: payment.bookingId,
          amount: payment.paymentData ? payment.paymentData.amount : 'N/A'
        } : 'No payment data'
      });
      
      if (!payment) {
        console.error('No payment data provided to processRazorpayFailure');
        return;
      }
      
      const { bookingId, onError } = payment;
      
      // Mark the booking as failed
      try {
        updateDoc(doc(db, 'bookings', bookingId), {
          status: 'failed',
          paymentStatus: 'failed',
          error: errorMessage,
          updatedAt: serverTimestamp(),
        });
      } catch (updateError) {
        console.error('Error updating booking status on failure:', updateError);
      }
      
      // Reset payment state
      setCurrentPayment(null);
      
      // Call the error callback
      if (typeof onError === 'function') {
        onError(errorMessage);
      } else {
        console.error('onError callback is not a function');
      }
    } catch (handlerError) {
      console.error('Error in processRazorpayFailure:', handlerError);
    }
  };
  
  // Keep the original handlers for backward compatibility
  const handleRazorpaySuccess = async (response) => {
    try {
      if (!currentPayment) {
        console.error('No current payment found when handling payment success');
        return;
      }
      
      await processRazorpaySuccess(response, currentPayment);
    } catch (error) {
      console.error('Error in handleRazorpaySuccess:', error);
      handleRazorpayFailure(error);
    }
  };
  
  const handleRazorpayFailure = (error) => {
    try {
      if (!currentPayment) {
        console.error('No current payment found when handling payment failure');
        return;
      }
      
      processRazorpayFailure(error, currentPayment);
    } catch (handlerError) {
      console.error('Error in handleRazorpayFailure:', handlerError);
    }
  };

  const value = {
    createPayment,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
} 